/* ─────────────────────────────────────────────────────────────
   Action! — Gold SVG icon set
   Each icon takes `size` (default per icon) and `color` (default
   'currentColor'), so it inherits the parent's CSS `color`. On the
   real site, set `color: var(--accent-gold)` on the parent so icons
   recolor with the active theme.
   Source: redesign-icons.jsx (design package) — paths copied verbatim.
   `...rest` is spread onto the <svg> so callers can pass aria-hidden,
   style, className, etc. without changing the verbatim path data.
   ───────────────────────────────────────────────────────────── */

export const IconClapper = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    {/* slate body */}
    <rect x="3" y="11" width="18" height="10" rx="1.5" stroke={color} strokeWidth="1.6"/>
    {/* clapper arm — hinged at top-left, raised open ~21° above the body */}
    <path d="M3 11L21 4L20.1 1.6L2.1 8.6Z" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
    {/* two stripe dividers on the arm */}
    <path d="M9.5 8.5L8.5 6.1M15.2 6.2L14.3 3.8" stroke={color} strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
)

export const IconSwords = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M14.5 17.5L4 7V3h4l10.5 10.5M13 19l6-6M16 16l4 4M5 17l4 4" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M19.5 3L22 5.5l-3 3L16.5 6M19 21l-3-3" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

export const IconHandshake = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M3 11l4-4 3 3 2-2 5 5-2 2-1-1-2 2-2-2-2 2-2-2-3-3z" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M14 8l4-4 3 3-4 4M10 7l-3-3" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
)

export const IconConfetti = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M3 21l5-12 8 8-12 5z" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M14 13l4-1M16 8l3-3M18 16l3 1M11 4l1-2M19 12l2 1" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="13" cy="6" r="0.8" fill={color}/>
    <circle cx="20" cy="8" r="0.8" fill={color}/>
  </svg>
)

export const IconDice = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <rect x="3" y="3" width="18" height="18" rx="3" stroke={color} strokeWidth="1.6"/>
    <circle cx="8" cy="8" r="1.2" fill={color}/>
    <circle cx="16" cy="8" r="1.2" fill={color}/>
    <circle cx="12" cy="12" r="1.2" fill={color}/>
    <circle cx="8" cy="16" r="1.2" fill={color}/>
    <circle cx="16" cy="16" r="1.2" fill={color}/>
  </svg>
)

export const IconScroll = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M5 4h11l3 3v13H8a3 3 0 01-3-3V4z" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M5 4a2 2 0 00-2 2v0a2 2 0 002 2M8 9h8M8 13h8M8 17h5" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
)

export const IconTicket = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M3 8a2 2 0 012-2h14a2 2 0 012 2v2a2 2 0 000 4v2a2 2 0 01-2 2H5a2 2 0 01-2-2v-2a2 2 0 000-4V8z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
    <path d="M10 8v2M10 13v3" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeDasharray="2 2"/>
  </svg>
)

export const IconChevronRight = ({ size = 16, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" {...rest}>
    <path d="M6 3l5 5-5 5" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

export const IconCheck = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M4 12l5 5L20 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

export const IconX = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M6 6l12 12M18 6L6 18" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
)

export const IconPlay = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" {...rest}>
    <path d="M6 4l10 6-10 6V4z" fill={color}/>
  </svg>
)

export const IconChart = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <rect x="3" y="13" width="4" height="8" rx="1" fill={color}/>
    <rect x="10" y="8" width="4" height="13" rx="1" fill={color}/>
    <rect x="17" y="4" width="4" height="17" rx="1" fill={color}/>
  </svg>
)

export const IconCopy = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" {...rest}>
    <rect x="6" y="4" width="10" height="12" rx="2" stroke={color} strokeWidth="1.8"/>
    <path d="M4 14V4a2 2 0 012-2h8" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
)

export const IconActor = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <circle cx="12" cy="8" r="3.6" stroke={color} strokeWidth="1.7"/>
    <path d="M5.5 20a6.5 6.5 0 0113 0" stroke={color} strokeWidth="1.7" strokeLinecap="round"/>
  </svg>
)

export const IconActress = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <circle cx="12" cy="8" r="3.6" stroke={color} strokeWidth="1.7"/>
    <path d="M5.5 20a6.5 6.5 0 0113 0" stroke={color} strokeWidth="1.7" strokeLinecap="round"/>
    <path d="M8.4 8.5c-1.1.6-1.9 1.8-2 3.5M15.6 8.5c1.1.6 1.9 1.8 2 3.5" stroke={color} strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
)

export const IconAny = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <circle cx="9" cy="8.5" r="3" stroke={color} strokeWidth="1.6"/>
    <path d="M3.5 19a5.5 5.5 0 0111 0" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M16 5.8a3 3 0 010 5.6M17 19a5.6 5.6 0 00-2.4-4.6" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
)

export const IconSearch = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <circle cx="11" cy="11" r="7" stroke={color} strokeWidth="1.7"/>
    <path d="M16.5 16.5L21 21" stroke={color} strokeWidth="1.7" strokeLinecap="round"/>
  </svg>
)

export const IconArrowRight = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M4 12h15M13 6l6 6-6 6" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

export const IconArrowLeft = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M20 12H5M11 6l-6 6 6 6" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

export const IconCut = ({ size = 16, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M3 9h18v11a1 1 0 01-1 1H4a1 1 0 01-1-1V9z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
    <path d="M3 9l2-5 16 0-2 5M8 4l-2 5M13 4l-2 5M18 4l-2 5" stroke={color} strokeWidth="1.4" strokeLinejoin="round"/>
  </svg>
)

export const IconSave = ({ size = 16, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M5 3h11l3 3v15H5V3z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
    <path d="M8 3v5h6V3M8 21v-6h8v6" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
  </svg>
)

/* ── Added for WinScreen rating tiers + circle action buttons ── */

export const IconTrophy = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M7 4h10v6a5 5 0 01-10 0V4z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
    <path d="M7 6H4v2a3 3 0 003 3M17 6h3v2a3 3 0 01-3 3M9 21h6M12 15v6" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
)

export const IconStar = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M12 2l3 7 7.5.5-5.5 5 1.7 7.5L12 18l-6.7 4 1.7-7.5-5.5-5L9 9l3-7z" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
)

export const IconReel = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.6"/>
    <circle cx="12" cy="12" r="2" fill={color}/>
    <circle cx="12" cy="6" r="1.2" fill={color}/><circle cx="12" cy="18" r="1.2" fill={color}/>
    <circle cx="6" cy="12" r="1.2" fill={color}/><circle cx="18" cy="12" r="1.2" fill={color}/>
  </svg>
)

export const IconRibbon = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <circle cx="12" cy="9" r="6" stroke={color} strokeWidth="1.6"/>
    <path d="M9 14l-3 7 6-3 6 3-3-7" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
    <circle cx="12" cy="9" r="2.4" stroke={color} strokeWidth="1.4"/>
  </svg>
)

export const IconReplay = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M3 12a9 9 0 109-9 9 9 0 00-7 3.5M4 3v3.5h3.5" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

export const IconHome = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M3 11l9-8 9 8v9a1 1 0 01-1 1h-5v-6h-6v6H4a1 1 0 01-1-1v-9z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
  </svg>
)

export const IconPlus = ({ size = 24, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M12 5v14M5 12h14" stroke={color} strokeWidth="2" strokeLinecap="round"/>
  </svg>
)

export const IconMessage = ({ size = 24, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M4 5h16a1 1 0 011 1v10a1 1 0 01-1 1H9l-4 4v-4H4a1 1 0 01-1-1V6a1 1 0 011-1z" stroke={color} strokeWidth="1.7" strokeLinejoin="round"/>
    <path d="M8 10h8M8 13h5" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
)

export const IconLock = ({ size = 16, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <rect x="5" y="11" width="14" height="9" rx="2" stroke={color} strokeWidth="1.7" strokeLinejoin="round"/>
    <path d="M8 11V8a4 4 0 018 0v3" stroke={color} strokeWidth="1.7" strokeLinecap="round"/>
    <circle cx="12" cy="15.5" r="1.2" fill={color}/>
  </svg>
)

/* ── Added for Party Mode redesign — gold SVGs replacing the emoji icons. ── */

/* Stopwatch — Speed Run */
export const IconStopwatch = ({ size = 24, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <circle cx="12" cy="13.5" r="7.5" stroke={color} strokeWidth="1.6"/>
    <path d="M9.5 2.5h5" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M12 2.5V6" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M18.5 7.5l1.4-1.4" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M12 13.5V9.8" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
)

/* Two figures + versus divider — H2H (team vs team) */
export const IconVersus = ({ size = 24, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <circle cx="6.5" cy="8" r="2.6" stroke={color} strokeWidth="1.5"/>
    <path d="M2.5 18c0-2.5 1.8-4 4-4s4 1.5 4 4" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="17.5" cy="8" r="2.6" stroke={color} strokeWidth="1.5"/>
    <path d="M13.5 18c0-2.5 1.8-4 4-4s4 1.5 4 4" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M12 4v16" stroke={color} strokeWidth="1.2" strokeDasharray="2 2.4" strokeLinecap="round"/>
  </svg>
)

/* Two figures united (no divider) — Co-Op. Pairs with IconVersus. */
export const IconTeam = ({ size = 24, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...rest}>
    <circle cx="15.5" cy="8.5" r="2.6"/>
    <path d="M20 18.5c0-3-2-5-4.5-5"/>
    <circle cx="9" cy="8" r="3"/>
    <path d="M3.5 18.5c0-3.2 2.5-5.3 5.5-5.3s5.5 2.1 5.5 5.3"/>
  </svg>
)

/* Director's chair — Director's Cut */
export const IconDirectorChair = ({ size = 24, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M5.5 4.5h13M5.5 8h13" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M6.5 4v7M17.5 4v7" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M4.5 11h15" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M7 11l9 9M17 11l-9 9" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8.7 16.7h6.6" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
)

/* ── Added for Party Mode shared core ── */

/* Crown — host badge / winner */
export const IconCrown = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M3 7l4 4 5-6 5 6 4-4-1.5 12H4.5L3 7z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
    <path d="M4.5 19h15" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
)

/* Clock — live timer */
export const IconClock = ({ size = 22, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.7"/>
    <path d="M12 7v5l3.5 2" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

/* Eye — "watch the host screen" */
export const IconEye = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
    <circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.6"/>
  </svg>
)

/* Pencil — edit affordance */
export const IconPencil = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
  </svg>
)

/* Flame — streaks */
export const IconFlame = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
  </svg>
)

/* Camera — change photo */
export const IconCamera = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
    <circle cx="12" cy="13" r="3" stroke={color} strokeWidth="1.6"/>
  </svg>
)

/* Calendar — Daily / scheduled puzzle */
export const IconCalendar = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <rect x="3.5" y="5" width="17" height="16" rx="2" stroke={color} strokeWidth="1.6"/>
    <path d="M3.5 9.5h17M8 3v4M16 3v4" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
)

/* TV — television series badge */
export const IconTv = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <rect x="3" y="7" width="18" height="12" rx="2" stroke={color} strokeWidth="1.6"/>
    <path d="M8 3.5l4 3.5 4-3.5" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

/* ── App-wide emoji→SVG sweep: canonical icons ── */

/* Target — goal actor / spoiler */
export const IconTarget = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.6"/>
    <circle cx="12" cy="12" r="4.5" stroke={color} strokeWidth="1.6"/>
    <circle cx="12" cy="12" r="1.4" fill={color}/>
  </svg>
)

/* Cue card — hint card */
export const IconCueCard = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <rect x="5" y="3" width="14" height="18" rx="2" stroke={color} strokeWidth="1.6"/>
    <path d="M12 7.5l2.2 3.3-2.2 3.2-2.2-3.2L12 7.5z" stroke={color} strokeWidth="1.3" strokeLinejoin="round"/>
  </svg>
)

/* Bolt — speed / reaction */
export const IconBolt = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
  </svg>
)

/* Open book — How to Play */
export const IconBook = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M12 6C10.3 4.6 7.4 4.1 4 4.6v13.8C7.4 17.9 10.3 18.4 12 19.8M12 6c1.7-1.4 4.6-1.9 8-1.4v13.8c-3.4-.5-6.3 0-8 1.4M12 6v13.8" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
  </svg>
)

/* Lightbulb — tips */
export const IconLightbulb = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M9 17.5h6M10 20.5h4" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M12 3.5a6 6 0 00-3.8 10.6c.7.6.9 1.1.9 2.4h5.8c0-1.3.2-1.8.9-2.4A6 6 0 0012 3.5z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
  </svg>
)

/* Thermometer — cue temperature (warm / getting-warmer) */
export const IconThermometer = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M14 14.6V5a2 2 0 10-4 0v9.6a4 4 0 104 0z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
    <circle cx="12" cy="17" r="1.6" fill={color}/>
  </svg>
)

/* Snowflake — cue temperature (freezing) */
export const IconSnowflake = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M12 2.5v19M3.8 7.25l16.4 9.5M20.2 7.25L3.8 16.75" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M9.5 4.2L12 6l2.5-1.8M9.5 19.8L12 18l2.5 1.8" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

/* Drama mask — party / perform / pass */
export const IconMasks = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M4 4.5h16v6.5a8 8 0 01-16 0V4.5z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
    <path d="M8.5 9c.9.7 1.9.7 2.8 0M12.7 9c.9.7 1.9.7 2.8 0" stroke={color} strokeWidth="1.3" strokeLinecap="round"/>
    <path d="M9 13.5c1.4 1.3 4.6 1.3 6 0" stroke={color} strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
)

/* Share — share / announce */
export const IconShare = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <circle cx="6" cy="12" r="2.6" stroke={color} strokeWidth="1.6"/>
    <circle cx="17.5" cy="5.5" r="2.6" stroke={color} strokeWidth="1.6"/>
    <circle cx="17.5" cy="18.5" r="2.6" stroke={color} strokeWidth="1.6"/>
    <path d="M8.3 10.8l6.9-3.9M8.3 13.2l6.9 3.9" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
)

/* Warning — caution */
export const IconWarning = ({ size = 20, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M12 3.2L1.8 20.5h20.4L12 3.2z" stroke={color} strokeWidth="1.6" strokeLinejoin="round"/>
    <path d="M12 9.5v4.5" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    <circle cx="12" cy="17" r="1" fill={color}/>
  </svg>
)

/* Filled dot — difficulty rating (pass color: green / amber / red) */
export const IconDot = ({ size = 12, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <circle cx="12" cy="12" r="7" fill={color}/>
  </svg>
)

/* ── Added for Profile → Account buttons redesign ── */

/* Sign out — arrow leaving a doorway */
export const IconSignOut = ({ size = 18, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M15 4h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-2" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 17l5-5-5-5M15 12H3" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

/* Download — arrow into a tray */
export const IconDownload = ({ size = 18, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M12 3v12M7 10l5 5 5-5" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5 19h14" stroke={color} strokeWidth="1.7" strokeLinecap="round"/>
  </svg>
)

/* Trash — delete */
export const IconTrash = ({ size = 18, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 11v6M14 11v6" stroke={color} strokeWidth="1.7" strokeLinecap="round"/>
  </svg>
)

/* Instagram — gold line-art (intentionally not the full-color glyph) */
export const IconInstagram = ({ size = 22, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <rect x="3" y="3" width="18" height="18" rx="5.4" stroke={color} strokeWidth="1.7"/>
    <circle cx="12" cy="12" r="4.2" stroke={color} strokeWidth="1.7"/>
    <circle cx="17.2" cy="6.8" r="1.15" fill={color}/>
  </svg>
)

/* App Store — stylized "A" in a rounded square */
export const IconAppStore = ({ size = 22, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
    <rect x="3" y="3" width="18" height="18" rx="5.4" stroke={color} strokeWidth="1.7"/>
    <path d="M8.2 15.6l3.05-5.28M15.8 15.6l-3.05-5.28L11.1 7.6" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 15.6h6.6" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M7.4 13h4" stroke={color} strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
)

/* Apple logo — filled glyph (used on the App Store CTA) */
export const IconApple = ({ size = 16, color = 'currentColor', ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} {...rest}>
    <path d="M16.4 12.7c0-2.3 1.9-3.4 2-3.45-1.1-1.6-2.8-1.82-3.4-1.84-1.45-.15-2.83.85-3.56.85-.73 0-1.86-.83-3.06-.81-1.57.02-3.02.91-3.83 2.32-1.63 2.83-.42 7.02 1.17 9.32.78 1.12 1.7 2.38 2.91 2.34 1.17-.05 1.61-.76 3.02-.76 1.41 0 1.81.76 3.05.73 1.26-.02 2.06-1.14 2.83-2.27.89-1.3 1.26-2.56 1.28-2.62-.03-.01-2.45-.94-2.48-3.73z M14.1 5.3c.64-.78 1.07-1.86.95-2.94-.92.04-2.04.61-2.7 1.39-.59.69-1.11 1.79-.97 2.85 1.03.08 2.08-.52 2.72-1.3z"/>
  </svg>
)
